import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { SidebarModal } from '../../components/common/Sidebar/Sidebar';
import {
  formatUSD,
  formatDecimalNumber,
  getWalletBalance,
  getAkashaWalletBalanceFromUltron,
  getULXWalletBalance
} from '../../helpers/helperFunctions';
import { CaptionText, H3, P1, P3 } from '../../styles/FontStyles';
import { Button, Flexed } from '../../styles/GlobalComponents';
import { color } from '../../styles/GlobalStyles';
import {
  Icon,
  IconPortfolioDistribution,
  IconPortfolioOrders,
  IconPortfolioWallet
} from '../../styles/Icons';
import { settingsInit } from '../Settings/Settings.actions';

import { BoxPortfolio, SummaryGrid } from './Portfolio.styled';
import { Wallet } from './Wallet';
import { Withdraw } from './Withdraw/Withdraw';
import { WithdrawSteps, WithdrawAvailability } from './Portfolio.types';

import { getAkashaWalletSummary } from '../Marketplace/Marketplace.actions';
import { Desktop, Mobile } from '../../App';
import styled from 'styled-components';

import assetsBanner from '../../assets/images/backoffice/assets/assetsBanner.png';
import assetsBannerMobile from '../../assets/images/backoffice/assets/assetsBannerMobile.png';

import transferInProgressBanner from '../../assets/images/backoffice/transactionInProgress/transactionInProgressBanner.png';
import transferInProgressBannerMobile from '../../assets/images/backoffice/transactionInProgress/transactionInProgressBannerMobile.png';

interface WalletSummaryProps {
  setNoKyc: any;
  setNoTwoFa: any;
  setTwoFa: any;
  verificationStatus: boolean;
  isTransferSent: boolean;
  setCode2Fa: Dispatch<SetStateAction<string>>;
  setCode: Dispatch<SetStateAction<string>>;
}

export const WalletSummary = ({
  setNoKyc,
  setNoTwoFa,
  setTwoFa,
  verificationStatus,
  isTransferSent,
  setCode2Fa,
  setCode
}: WalletSummaryProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('my_hubs');

  const [step, setStep] = useState<WithdrawSteps>(WithdrawSteps.INITIAL);
  const [withdraw, setWithdraw] = useState(false);
  const [withdrawAvailabilitySteps, setWithdrawAvailabilitySteps] = useState(
    WithdrawAvailability.ULX
  );
  const [ulxBalance, setULXBalance] = useState<any>(0);
  const [wlxBalance, setWlxBalance] = useState<any>(0);
  const [usdtBalance, setUsdtBalance] = useState<any>(0);

  const { walletSummary, withdrawAvailability } = useAppSelector(
    (state) => state.portfolio
  );

  const { kycStatus, user } = useAppSelector((state) => state.auth.userInfo);

  const getBalance = async () => {
    if (!walletSummary || !walletSummary.userAddress) return;

    const wULXAddress = process.env.REACT_APP_WULX_ADDRESS!;
    const wULXDecimals = process.env.REACT_APP_WULX_DECIMALS!;

    const wulxBalance = await getWalletBalance(
      walletSummary.userAddress,
      wULXAddress,
      wULXDecimals
    );
    const usdtBalance = await getWalletBalance(
      walletSummary.userAddress,
      process.env.REACT_APP_USDT_ADDRESS!,
      process.env.REACT_APP_USDT_DECIMALS!
    );

    const ulxBalance = await getULXWalletBalance(walletSummary.userAddress);

    setWlxBalance(wulxBalance);
    setUsdtBalance(usdtBalance);

    setULXBalance(ulxBalance);
  };

  useEffect(() => {
    getBalance();
    dispatch(getAkashaWalletSummary());
  }, [dispatch]);

  const TransferInProgressBanner = styled.div`
    width: 100%;
    margin-bottom: 40px;
    overflow: hidden;
    position: relative;
    img {
      width: 100%;
      border-radius: 12px;
    }
    button {
      position: absolute;
      right: 50px;
      bottom: 50px;
      width: 250px;
      scale: 1.5;
      transform-origin: right bottom;
      font-weight: 500;
      font-size: 13px;
    }

    @media (max-width: 800px) {
      button {
        position: absolute;
        right: 50%;
        bottom: 20px;
        transform: translateX(50%);
        width: 250px;
        scale: 1;
        font-weight: 500;
        font-size: 13px;
      }
    }
  `;

  return (
    <>
      {walletSummary?.userAddress && (
        <>
          <TransferInProgressBanner>
            <Desktop>
              <img
                src={isTransferSent ? transferInProgressBanner : assetsBanner}
                alt='assetsBanner'
              />
            </Desktop>
            <Mobile>
              <img
                src={
                  isTransferSent
                    ? transferInProgressBannerMobile
                    : assetsBannerMobile
                }
                alt='assetsBannerMobile'
              />
            </Mobile>
            <Button
              negative
              onClick={() => {
                setCode2Fa('');
                setCode('');
                if (!isTransferSent) {
                  if (!kycStatus?.isKycCompleted) {
                    setNoKyc(true);
                  } else if (!verificationStatus) {
                    setNoTwoFa(true);
                  } else if (verificationStatus) {
                    setTwoFa(true);
                  }
                }
              }}>
              {isTransferSent
                ? `Check Your Email ${user?.email}`
                : 'Transfer All Assets'}
            </Button>
          </TransferInProgressBanner>
        </>
      )}
      <Flexed mb='20' between alignEnd rowSM alignStartSM gap='16px'>
        <H3>{t('my_wallet_summary')}</H3>
        <Wallet />
      </Flexed>
      <SummaryGrid>
        <BoxPortfolio>
          <Icon mr='16' mrSM='8' color={color.colors.secondary100}>
            <IconPortfolioOrders />
          </Icon>
          <div>
            <CaptionText caps bold color={color.colors.neutral500} mb='10'>
              {t('total_personal_orders')}
            </CaptionText>

            <P1 bold flex alignEnd gap='5px'>
              {formatUSD(walletSummary?.totalOrder)} <P3 mb='1'>USDT</P3>
            </P1>
          </div>
        </BoxPortfolio>

        <BoxPortfolio hasButton>
          <Flexed center>
            <Icon mr='16' mrSM='8' color={color.colors.secondary100}>
              <IconPortfolioWallet />
            </Icon>
            <div>
              <CaptionText caps bold color={color.colors.neutral500} mb='10'>
                {t('available_in_wallet')}
              </CaptionText>
              <P1 bold flex alignEnd gap='5px'>
                {formatUSD(Number(usdtBalance) ? Number(usdtBalance) : 0)}
                <P3 mb='1'>uUSDT</P3>
              </P1>
            </div>
          </Flexed>

          <Button
            w='100px'
            h='34px'
            disabled={walletSummary && Number(usdtBalance) < 1}
            onClick={() => {
              setWithdrawAvailabilitySteps(WithdrawAvailability.USDT);
              if (withdrawAvailability?.isAvailable) {
                setWithdraw(true);
                setStep(1);
                dispatch(settingsInit());
              } else {
                setWithdraw(true);
                setStep(9);
              }
            }}>
            {t('withdraw')}
          </Button>
        </BoxPortfolio>

        <BoxPortfolio hasButton>
          <Flexed center>
            <Icon mr='16' mrSM='8' color={color.colors.secondary100}>
              <IconPortfolioWallet />
            </Icon>
            <div>
              <CaptionText caps bold color={color.colors.neutral500} mb='10'>
                {t('available_in_wallet')}
              </CaptionText>
              <P1 bold flex alignEnd gap='5px'>
                {formatUSD(Number(ulxBalance))}
                <P3 mb='1'>ULX</P3>
              </P1>
            </div>
          </Flexed>
        </BoxPortfolio>

        <BoxPortfolio hasButton>
          <Flexed center>
            <Icon mr='16' mrSM='8' color={color.colors.secondary100}>
              <IconPortfolioWallet />
            </Icon>
            <div>
              <CaptionText caps bold color={color.colors.neutral500} mb='10'>
                {t('available_in_wallet')}
              </CaptionText>
              <P1 bold flex alignEnd gap='5px'>
                {formatUSD(Number(wlxBalance))}
                <P3 mb='1'>wULX</P3>
              </P1>
            </div>
          </Flexed>
          <Button
            w='100px'
            h='34px'
            disabled={walletSummary && Number(wlxBalance) < 1}
            onClick={() => {
              setWithdrawAvailabilitySteps(WithdrawAvailability.ULX);
              if (withdrawAvailability?.isAvailable) {
                setWithdraw(true);
                setStep(1);
                dispatch(settingsInit());
              } else {
                setWithdraw(true);
                setStep(9);
              }
            }}>
            {t('withdraw')}
          </Button>
        </BoxPortfolio>
      </SummaryGrid>

      <SidebarModal
        isOpen={withdraw}
        setOpen={setWithdraw}
        title={`${
          withdrawAvailabilitySteps === WithdrawAvailability.ULX
            ? 'wULX'
            : 'uUSDT'
        } ${t('withdrawal')}`}
        sidebarWidth={'508px'}>
        <Withdraw
          step={step}
          setStep={setStep}
          setWithdraw={setWithdraw}
          withdrawAvailabilitySteps={withdrawAvailabilitySteps}
        />
      </SidebarModal>
    </>
  );
};
